// @flow
import * as React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';

import type { TPlayerWithFixture } from '../../modules/types/player';
import type {
	TCoachStatsListReducer,
	TPlayerStatusesReducer,
	TPosition,
	TRootStore,
	TSquad,
	TUser,
	TRound
} from '../../modules/types';


import * as actions from "../../modules/actions";
import * as selectors from "../../modules/selectors";

type TableColumns = Array<{ key: string, prop: string, prop_sub?: string, tooltip?: string }>;

type TDraftProps = {
	match: {
		params: {
			league_id: string
		}
	},
	players: Array<TPlayerWithFixture>,
	squads: Array<TSquad>,
	showDraftLeague: typeof actions.leagueDraft.showDraftLeague,
	fetchPlayers: typeof actions.players.fetchPlayers,
	fetchCoachPlayers: typeof actions.players.fetchCoachPlayers,
	fetchRounds: typeof actions.rounds.fetchRounds,
	fetchSquads: typeof actions.squads.fetchSquads,
	fetchVenues: typeof actions.venues.fetchVenues,
	fetchFavourites: typeof actions.players.fetchFavourites,
	fetchPreDraftList: typeof actions.teamsDraft.fetchPreDraftList,
	postPreDraftList: typeof actions.teamsDraft.postPreDraftList,
	addPlayerToPreDraftList: typeof actions.teamsDraft.addPlayerToPreDraftList,
	removePlayerFromPreDraftList: typeof actions.teamsDraft.removePlayerFromPreDraftList,
	updatePlayerPreDraftListOrder: typeof actions.teamsDraft.updatePlayerPreDraftListOrder,
	requestServerAndLocalTimeDiff: typeof actions.leagueDraft.requestServerAndLocalTimeDiff,
	is_pending_players: boolean,
	positions: Array<TPosition>,
	playerStatuses: TPlayerStatusesReducer,
	league: Object,
	pre_draft_list: Array<number>,
	pre_draft_list_size: number,
	coach_stats_list: TCoachStatsListReducer,
	actual_round: TRound,
	user: TUser,
	pub_nub_channel: string,
	server_time_diff: number,
	has_assistant_coach: boolean,
}

type TFilters = {
	search: string,
	by_squad: string,
	by_position: string,
	by_status: string,
	by_dpp: boolean,
	by_favourites: boolean,
	by_ignored: boolean,
	by_drafted: boolean
}

type State = {
	sort_by: string,
	order_by_desc: boolean,
	filters: TFilters,
	players_show_limit: number,
	players_show_limit_step: number,
	is_coach_active: boolean,
	coachSelectValue: string,
}

export type TDraftPassedProps = TDraftProps & State & {
	load_more_is_hidden: boolean,
	has_filters: boolean,
	players_filter: Function,
	table_keys: TableColumns,
	is_dnd_allowed: boolean,
	order_by_desc?: boolean,
	sort_by?: string,
	onSortColumnChange: Function,
	onToggleOrder: Function,
	onResetFilterClick: Function,
	isActiveTab: Function,
	onFiltersChange: Function,
	onFilterByPosition: Function,
	onLoadMoreClick: Function,
	onTabChange: Function,
	onSearchSubmit: Function,
	onChangePlayerOrder: Function,
	updatePlayerPreDraftListOrder: Function,
	onSelectChange: Function,
	orderByComparator: Function,
	onDrop: Function,
	onDragEnd: Function,
	onDragOver: Function,
	onDragEnter: Function,
	onDragStart: Function,
	onDragLeave: Function,
	onEnableDrag: Function,
	onDisableDrag: Function,
}

const swapArrayElements = function (array = [], from = 0, to = 0) {
	const dest = array.slice();

	dest.splice(to, 0, dest.splice(from, 1)[0]);

	return dest;
};

const TOTAL_ROUNDS_SIZE = parseInt(process.env.REACT_APP_TOTAL_ROUNDS_SIZE, 10);

const commonTableColumns = [
	{ key: 'GP', prop: 'stats.games_played', tooltip: 'Games Played' },
	{ key: 'AVG', prop: 'stats.avg_points', tooltip: 'Average Round Score' },
	{ key: 'TP', prop: 'stats.total_points', tooltip: 'Total Points Scored' },
];

const getPubNubChannelName = props => (
	String(process.env.REACT_APP_PUBNUB_CHANNEL_PREFIX) + _.get(props, 'match.params.league_id')
);

export const withDraftCore = (WrappedComponent: React.ComponentType<Object>) => {
	class DraftCore extends React.Component<TDraftProps, State> {
		state = {
			players_show_limit: 50,
			players_show_limit_step: 20,
			sort_by: 'draft_order',
			order_by_desc: false,
			is_coach_active: false,
			coachSelectValue: 'coaches-choice',
			filters: {
				search: '',
				by_squad: '',
				by_position: '',
				by_status: '',
				by_dpp: false,
				by_favourites: false,
				by_ignored: false,
				by_drafted: false,
			},
		};

		static defaultProps = {
			players: [],
			pre_draft_list_size: 0,
		};

		constructor(props, state) {
			super(props, state);

			this.transfer_player_id = undefined;
			this.last_dnd_enter = undefined;
			this.initial_mouse_y_cord = 0;
			this.current_mouse_y_cord = 0;

			_.bindAll(this, [
				'onSortColumnChange',
				'onToggleOrder',
				'onResetFilterClick',
				'isActiveTab',
				'onFiltersChange',
				'onFilterByPosition',
				'onLoadMoreClick',
				'onTabChange',
				'onSearchSubmit',
				'onChangePlayerOrder',
				'updatePlayerPreDraftListOrder',
				'onSelectChange',
				'orderByComparator',
				'onDrop',
				'onDragStart',
				'onDragEnd',
				'onDragEnter',
				'onDragOver',
			]);
		}
		

		transfer_player_id: number | typeof undefined;
		last_dnd_enter: Object | typeof undefined;
		initial_mouse_y_cord: number;
		current_mouse_y_cord: number;

		onSortColumnChange(column_value) {
			const { sort_by, order_by_desc } = this.state;
			const default_order = !['stats.adp', 'draft_order'].includes(column_value);

			this.setState({
				sort_by: column_value,
				order_by_desc: _.eq(sort_by, column_value) ? !order_by_desc : default_order,
			});
		}

		onToggleOrder(value) {
			this.setState({
				order_by_desc: (
					_.isUndefined(value) ? !this.state.order_by_desc : value
				)
			});
		}

		onResetFilterClick() {
			this.setState({
				sort_by: 'stats.proj_avg',
				order_by_desc: true
			});
		}

		get season_year() {
			const CURRENT_YEAR = parseInt(process.env.REACT_APP_CURRENT_YEAR || "2025",10) ;
			const { actual_round } = this.props,
				current_year = CURRENT_YEAR === new Date().getFullYear() ? new Date().getFullYear() : CURRENT_YEAR;

			if(_.isEmpty(actual_round)) {
				return current_year;
			}

			const { id, status } = actual_round,
				is_old_season = id === 1 && status === 'scheduled';

			return is_old_season ? current_year : current_year + 1;
		}

		get actual_round_id() {
			const { actual_round } = this.props;

			if(_.isEmpty(actual_round)) {
				return 1;
			}

			return actual_round.id;
		}
	
		get table_columns() {
			const
				actual_round_id = this.actual_round_id,
				first_round_id = _.min([actual_round_id, TOTAL_ROUNDS_SIZE]),
				second_round_id = _.min([actual_round_id + 1, TOTAL_ROUNDS_SIZE]),
				third_round_id = _.min([actual_round_id + 2, TOTAL_ROUNDS_SIZE]);

			return {
				basic: [
					...commonTableColumns,
					{
						key: 'L5 AVG',
						prop: 'stats.last_5_avg',
						tooltip: 'Average Score for Last 5 Games'
					},
					{ key: 'ADP', prop: 'stats.adp', tooltip: 'Average Draft Position' },
				],
				coach: {
					'coaches-choice': [
						...commonTableColumns,
						{ key: 'ADP', prop: 'stats.adp', tooltip: 'Average Draft Position' },
						{
							key: '% Owned',
							prop: 'stats.draft_owned_by',
							tooltip: '% Owned by Total Draft Teams'
						},
						{
							key: `${this.season_year} Proj AVG`,
							prop: 'stats.proj_avg',
							tooltip: 'Projected Season Average'
						},
					],
					'consistency': [
						...commonTableColumns,
						{ key: 'L5 AVG PTS', prop: 'stats.last_5_avg', tooltip: 'L5 AVG PTS' },
						{
							key: 'Cons Rating',
							prop: 'stats.consistency',
							tooltip: 'Consistency Rating'
						},
						{
							key: '20 < AVG',
							prop: 'stats.in_20_avg',
							tooltip: 'No. of Scores 20 Less than Average Score'
						},
						{
							key: '20 > AVG',
							prop: 'stats.out_20_avg',
							tooltip: 'No. of Scores 20 Greater than Average Score' },
					],
					'opposition': [
						...commonTableColumns,
						{
							key: `Rd ${first_round_id} OPP AVG`,
							prop: 'stats.opp_avg_one_round_after_current',
							prop_sub: 'stats.opp_one_round_after_current',
							tooltip: 'Average Against Upcoming Opponent'
						},
						{
							key: `Rd ${second_round_id} OPP AVG`,
							prop: 'stats.opp_avg_two_rounds_after_current',
							prop_sub: 'stats.opp_two_rounds_after_current',
							tooltip: 'Average Against Upcoming Opponent in 2 Weeks'
						},
						{
							key: `Rd ${third_round_id} OPP AVG`,
							prop: 'stats.opp_avg_three_round_after_current',
							prop_sub: 'stats.opp_three_rounds_after_current',
							tooltip: 'Average Against Upcoming Opponent in 3 Weeks'
						},
						{
							key: 'N3 OPP AVG',
							prop: 'stats.opp_avg_3_rounds',
							tooltip: 'Average Against Next 3 Opponents'
						},
					],
					'venues': [
						...commonTableColumns,
						{
							key: `Rd ${first_round_id} VEN AVG`,
							prop: 'stats.venue_avg_one_round_after_current',
							prop_sub: 'stats.venue_one_round_after_current',
							tooltip: 'Average at Upcoming Game Venue'
						},
						{
							key: `Rd ${second_round_id} VEN AVG`,
							prop: 'stats.venue_avg_two_rounds_after_current',
							prop_sub: 'stats.venue_two_rounds_after_current',
							tooltip: 'Average at Upcoming Game Venue in 2 Weeks'
						},
						{
							key: `Rd ${third_round_id} VEN AVG`,
							prop: 'stats.venue_avg_three_round_after_current',
							prop_sub: 'stats.venue_three_rounds_after_current',
							tooltip: 'Average at Upcoming Game Venue in 3 Weeks'
						},
						{
							key: 'N3 VEN AVG',
							prop: 'stats.venue_avg_3_rounds',
							tooltip: 'Average at Next Three Game Venues'
						},
					]
				},
			};
		};

		componentDidMount() {
			const {
				user,
				fetchVenues,
				fetchSquads,
				fetchRounds,
				fetchPlayers,
				fetchCoachPlayers,
				fetchFavourites,
				fetchPreDraftList,
				showDraftLeague,
				requestServerAndLocalTimeDiff,
				match: { params: { league_id } },
			} = this.props;

			if(_.isUndefined(league_id)) {
				throw new Error(
					'The "match.params.league_id" field is missed in props.' +
					' Component should be wrapped by "withRouter" function' +
					' or be part of <Router> component'
				);
			}

			requestServerAndLocalTimeDiff();
			showDraftLeague({ id: league_id });
			fetchPreDraftList({ league_id });
			fetchRounds();
			fetchSquads();
			fetchVenues();
			fetchFavourites();
			fetchPlayers();

			if (Boolean(user.assistant_coach)) {
				fetchCoachPlayers();
			}
		}

		componentDidUpdate(prevProps: TDraftProps): void {
			const {
				fetchPreDraftList,
				showDraftLeague,
				match: { params: { league_id } },
			} = this.props;

			if(!_.isEqual(prevProps.match.params.league_id, league_id)) {
				showDraftLeague({ id: league_id });
				fetchPreDraftList({ league_id });
			}
		}

		isActiveTab(sort_prop) {
			const { sort_by } = this.state;
			return _.eq(sort_by, sort_prop) ? 'active' : '';
		}

		accumulateFormInputsValueByName(accumulator: Object, formElement: Object) {
			const { type, name, checked, value } = formElement;

			if (_.eq(type, 'submit')) {
				return accumulator;
			}

			if (!name) {
				throw new Error('All form inputs should be have "name" attribute!');
			}

			accumulator[name] = _.eq(type, 'checkbox') ? checked : value;

			return accumulator;
		}

		formElementToKeyValueObject(form) {
			return Array.from(form.elements).reduce(this.accumulateFormInputsValueByName, {});
		}

		onFiltersChange(event) {
			const { filters } = this.state;

			const {
				order_by_desc,
				...rest_filters
			} = this.formElementToKeyValueObject(event.currentTarget);

			if(!_.isUndefined(order_by_desc)) {
				this.onToggleOrder(order_by_desc);
			}

			this.setState({
				filters: {
					...filters,
					...rest_filters,
				},
			});
		}

		onFilterByPosition(position_key) {
			const { filters } = this.state;

			this.setState({
				filters: {
					...filters,
					by_position: position_key === 'bench' ? '' : position_key
				}
			});
		}

		onLoadMoreClick() {
			const { players_show_limit, players_show_limit_step } = this.state;

			this.setState({
				players_show_limit: players_show_limit + players_show_limit_step,
			});
		}

		onTabChange({ currentTarget }){
			const { isCoach } = currentTarget.dataset;
			this.setState({ is_coach_active: Boolean(isCoach) });
		}

		onSearchSubmit(event) {
			event.preventDefault();
		}

		updatePlayerPreDraftListOrder({ currentTarget }) {
			const { order, id } = currentTarget.dataset;
			const { league, updatePlayerPreDraftListOrder } = this.props;
			const new_order = parseInt(order, 10);

			if (!_.isFinite(new_order)) {
				return;
			}

			updatePlayerPreDraftListOrder({
				player_id: id,
				new_order: new_order,
				team_id: league.team_id,
			});
		}

		onChangePlayerOrder({ currentTarget }) {
			const { id } = currentTarget.dataset;
			const order = currentTarget.value;

			this.updatePlayerPreDraftListOrder({
				currentTarget: { dataset: { id, order } },
			});
		}

		onSelectChange({ currentTarget }){
			this.setState({ coachSelectValue: currentTarget.value });
		}

		get table_keys() {
			const { is_coach_active, coachSelectValue } = this.state,
				key = is_coach_active ? `coach.${coachSelectValue}` : 'basic';

			return _.get(this.table_columns, key, []);
		}

		orderByComparator(player_one, player_two) {
			const
				{ sort_by, order_by_desc } = this.state,
				order = order_by_desc ? 1 : -1,
				minimal = Number.MAX_SAFE_INTEGER * order,
				getResult = (condition, min, value) => condition(value) ? min : value,
				isStringOrZero = val => _.isString(val) || _.eq(val, 0),
				getResultOrMin = _.partial(getResult, isStringOrZero, minimal * -1),
				a_result = getResultOrMin(_.get(player_one, sort_by)),
				b_result = getResultOrMin(_.get(player_two, sort_by)),
				is_bigger = a_result < b_result,
				is_less = a_result > b_result;

			if (is_bigger) {
				return order;
			}

			if (is_less) {
				return -1 * order;
			}

			return 0;
		}

		get has_filters() {
			return _.size(_.values(this.state.filters).filter(_.identity));
		}

		get players_filter() {
			const { filters } = this.state;

			const {
				by_status,
				by_squad,
				by_favourites,
				by_position,
				by_dpp,
				by_ignored,
				search,
			} = filters;

			const maybeGetFilter = (value, filter) => value ? filter : _.identity;

			return _.overEvery([
				maybeGetFilter(by_status, _.matches({ status: by_status })),
				maybeGetFilter(by_squad, _.matches({ squad_id: parseInt(by_squad, 10) })),
				maybeGetFilter(by_favourites, _.property('is_favourite')),
				maybeGetFilter(by_ignored, _.matches({ draft_order: 0 })),
				maybeGetFilter(by_position, _.flow([
					_.property('positions'),
					_.partial(_.includes, _, parseInt(by_position, 10)),
				])),
				maybeGetFilter(by_dpp, ({ positions }) => _.eq(_.size(positions), 2)),
				maybeGetFilter(search, (({ first_name, last_name }) => (
					`${first_name} ${last_name}`.toLowerCase().includes(search.toLowerCase())
				))),
			]);
		}

		get load_more_is_hidden() {
			const { players_show_limit } = this.state;
			const {	players, is_pending_players } = this.props;

			return _.some([
				is_pending_players,
				this.has_filters,
				_.gt(players_show_limit, _.size(players))
			], _.identity);
		}

		get placeholder_class_name() {
			return 'dnd-placeholder';
		}

		insertAfter(elem, refElem) {
			const
				parent = refElem.parentNode,
				next = refElem.nextSibling;

			return next ? parent.insertBefore(elem, next) : parent.appendChild(elem);
		}

		insertBefore(elem, refElem) {
			const parent = refElem.parentNode;
			return parent.insertBefore(elem, refElem);
		}

		onDragOver(event: Object) {
			event.preventDefault();
			this.current_mouse_y_cord = event.pageY;
		}

		onDragEnter(event) {
			event.preventDefault();

			const
				target = event.currentTarget,
				player_id = parseInt(target.dataset.playerId, 10);

			this.last_dnd_enter = this.last_dnd_enter || target;

			const
				same_element = this.last_dnd_enter === target,
				draggable_player = this.transfer_player_id === player_id;

			if(same_element || draggable_player) {
				return;
			}

			const dnd_disabled = !JSON.parse(target.dataset.dragAndDropEnabled);

			this.removeOldPlaceholder();

			if(dnd_disabled) {
				return;
			}

			this.last_dnd_enter = target;

			const td = document.createElement('td');

			td.setAttribute("colspan", String(_.size(target.querySelectorAll('td'))));
			td.setAttribute("height", target.clientHeight);

			const tr = document.createElement('tr');

			tr.appendChild(td);

			tr.className = this.placeholder_class_name;
			tr.dataset.dragAndDropEnabled = 'true';
			tr.dataset.playerId = String(player_id);

			tr.addEventListener('drop', this.onDrop, true);
			tr.addEventListener('dragover', this.onDragOver, true);

			const
				is_drop_to_top = this.initial_mouse_y_cord >= this.current_mouse_y_cord,
				insertMethod = is_drop_to_top ? this.insertBefore : this.insertAfter;

			insertMethod.call(this, tr, target);
		}

		removeOldPlaceholder() {
			const placeholder = document.querySelector(`.${this.placeholder_class_name}`);

			if(placeholder) {
				placeholder.removeEventListener('drop', this.onDrop);
				placeholder.removeEventListener('dragover', this.onDragOver);

				const parent = placeholder.parentNode;

				if(parent) {
					parent.removeChild(placeholder);
				}

				this.last_dnd_enter = undefined;
			}
		}

		onDragLeave(event) {
			event.preventDefault();
		}

		onDragEnd(event) {
			event.preventDefault();
			this.removeOldPlaceholder();
			this.last_dnd_enter = undefined;
			this.transfer_player_id = undefined;
			this.initial_mouse_y_cord = 0;
			this.current_mouse_y_cord = 0;
		}

		onDrop(event: Object) {
			event.preventDefault();

			const target = event.currentTarget;

			if (!JSON.parse(target.dataset.dragAndDropEnabled)) {
				return this.removeOldPlaceholder();
			}

			const
				dragged_player_id = this.transfer_player_id,
				dropped_player_id = parseInt(target.dataset.playerId, 10),
				is_same_player = _.eq(dragged_player_id, dropped_player_id);

			if (is_same_player) {
				return this.removeOldPlaceholder();
			}

			this.swapPlayers(dragged_player_id, dropped_player_id);
			this.removeOldPlaceholder();
		}

		swapPlayers(dragged_player_id = 0, dropped_player_id = 0) {
			const { league: { team_id }, pre_draft_list, postPreDraftList } = this.props;

			postPreDraftList({
				team_id,
				list: swapArrayElements(
					pre_draft_list,
					_.indexOf(pre_draft_list, dragged_player_id),
					_.indexOf(pre_draft_list, dropped_player_id),
				),
			});
		}

		onDragStart(event) {
			const target = event.currentTarget;
			this.initial_mouse_y_cord = event.pageY;
			this.transfer_player_id = parseInt(target.dataset.playerId, 10);
			event.dataTransfer.setData('text', this.transfer_player_id);
		}

		onEnableDrag(event) {
			const
				target = event.currentTarget,
				parent = target.closest('tr');

			if (parent) {
				const { dragAndDropEnabled } = parent.dataset;
				parent.draggable = JSON.parse(dragAndDropEnabled);
			}
		}

		onDisableDrag(event) {
			const target = event.currentTarget;
			const parent = target.closest('tr');

			if (parent) {
				parent.draggable = false;
			}
		}

		render() {
			return (
				<WrappedComponent
					{...this.props}
					{...this.state}
					is_dnd_allowed={!this.has_filters && this.state.sort_by === 'draft_order'}
					load_more_is_hidden={this.load_more_is_hidden}
					has_filters={this.has_filters}
					players_filter={this.players_filter}
					table_keys={this.table_keys}
					order_by_desc={this.state.order_by_desc}
					onSortColumnChange={this.onSortColumnChange}
					onToggleOrder={this.onToggleOrder}
					onResetFilterClick={this.onResetFilterClick}
					isActiveTab={this.isActiveTab}
					onFiltersChange={this.onFiltersChange}
					onFilterByPosition={this.onFilterByPosition}
					onLoadMoreClick={this.onLoadMoreClick}
					onTabChange={this.onTabChange}
					onSearchSubmit={this.onSearchSubmit}
					onChangePlayerOrder={this.onChangePlayerOrder}
					updatePlayerPreDraftListOrder={this.updatePlayerPreDraftListOrder}
					onSelectChange={this.onSelectChange}
					orderByComparator={this.orderByComparator}
					onDrop={this.onDrop}
					onDragOver={this.onDragOver}
					onDragEnter={this.onDragEnter}
					onDragStart={this.onDragStart}
					onDragLeave={this.onDragLeave}
					onDragEnd={this.onDragEnd}
					onEnableDrag={this.onEnableDrag}
					onDisableDrag={this.onDisableDrag}
				/>
			);
		}
	}

	const mapStateToProps = (state: TRootStore, props) => {
		const pre_draft_list = selectors.teamDraft.getPreDraftList(state);
		return ({
			user: selectors.getUser(state),
			players: selectors.players.getPreDraftPlayers(state),
			squads: selectors.squads.getSquads(state),
			is_pending_players: state.players.is_pending,
			positions: selectors.positions.getPositionsArray(state),
			playerStatuses: state.playerStatuses,
			league: selectors.leagueDraft.getLeague(state, props),
			pre_draft_list,
			pre_draft_list_size: _.size(pre_draft_list),
			coach_stats_list: state.coachStatsList.filter(({ key }) => key !== 'form'),
			actual_round: selectors.rounds.getActualRound(state),
			pub_nub_channel: getPubNubChannelName(props),
			server_time_diff: state.leagues.server_time_diff,
			has_assistant_coach: Boolean(selectors.getUser(state).assistant_coach),
		});
	};

	const mapDispatchToProps = {
		showDraftLeague: actions.leagueDraft.showDraftLeague,
		fetchPlayers: actions.players.fetchPlayers,
		fetchRounds: actions.rounds.fetchRounds,
		fetchSquads: actions.squads.fetchSquads,
		fetchVenues: actions.venues.fetchVenues,
		fetchFavourites: actions.players.fetchFavourites,
		fetchCoachPlayers: actions.players.fetchCoachPlayers,
		fetchPreDraftList: actions.teamsDraft.fetchPreDraftList,
		addPlayerToPreDraftList: actions.teamsDraft.addPlayerToPreDraftList,
		removePlayerFromPreDraftList: actions.teamsDraft.removePlayerFromPreDraftList,
		updatePlayerPreDraftListOrder: actions.teamsDraft.updatePlayerPreDraftListOrder,
		requestServerAndLocalTimeDiff: actions.leagueDraft.requestServerAndLocalTimeDiff,
		postPreDraftList: actions.teamsDraft.postPreDraftList,
	};

	return connect(
		mapStateToProps,
		mapDispatchToProps,
	)(DraftCore);
};

export default withDraftCore;


// WEBPACK FOOTER //
// ./src/components/Draft/withDraftCore.js